import Button from "@components/Button";
import { AltText, Column, Row } from "@components/Layout";
import Waves from "@components/Waves";
import useRecordAudioClip from "@hooks/useRecordAudioClip";
import PlayButton from "@layouts/App/context/Player/components/PlayButton";
import type { IMediaRecorder } from "extendable-media-recorder";
import { FaRegularTrashCan } from "solid-icons/fa";
import { IoMic } from "solid-icons/io";
import { Show, type VoidComponent, createSignal, onCleanup } from "solid-js";
import Recording from "./components/Recording";
import { ButtonWrapper } from "./styled";

const Record: VoidComponent<{
	onAudio: Parameters<ReturnType<typeof useRecordAudioClip>>[0];
	label: string;
	maxLimit?: number;
	minLimit?: number;
	onPlay?: () => void;
	onDelete?: () => void;
}> = (props) => {
	const [recorder, setRecorder] = createSignal<IMediaRecorder>();
	const [maxTimer, setMaxTimer] = createSignal<NodeJS.Timeout>();
	const [minTimer, setMinTimer] = createSignal<NodeJS.Timeout>();
	const [canStop, setCanStop] = createSignal(true);
	const record = useRecordAudioClip();

	const handleStartRecording = (r?: IMediaRecorder) => {
		if (!r) return;
		setRecorder(r);
		r.start();

		if (props.minLimit) {
			setCanStop(false);
			setMinTimer(setTimeout(() => setCanStop(true), props.minLimit));
		}

		if (!props.maxLimit) return;

		clearTimeout(maxTimer());
		setMaxTimer(
			setTimeout(() => {
				recorder()?.stop();
				setRecorder(undefined);
			}, props.maxLimit),
		);
	};

	onCleanup(() => {
		clearTimeout(maxTimer());
		clearTimeout(minTimer());
	});

	return (
		<Column alignItems="center" justifyContent="center" gap="15px" padding="0 0 20px">
			<ButtonWrapper>
				<Show
					when={recorder()}
					keyed
					fallback={
						<Button diameter="100%" onclick={() => record(props.onAudio).then(handleStartRecording)}>
							<IoMic font-size="50px" />
						</Button>
					}
				>
					{(r) => (
						<Column
							w="100%"
							h="100%"
							onclick={() => {
								if (!canStop()) return;
								r.stop();
								clearTimeout(maxTimer());
								setRecorder(undefined);
							}}
							alignItems="center"
							justifyContent="center"
						>
							<Recording disabled={!canStop()} />
						</Column>
					)}
				</Show>
			</ButtonWrapper>
			<Row justifyContent="center">
				<AltText fontWeight="bold">{props.label}</AltText>
			</Row>
			<Show when={props.onPlay} keyed>
				{(onPlay) => (
					<Row gap="10px" margin="50px" alignItems="center">
						<Column flexShrink={0} w="max-content">
							<PlayButton alt onClick={onPlay} diameter="40px" />
						</Column>
						<Waves h="30px" count={13} progress={0} seed="" />
						<Show when={props.onDelete}>
							<Column flexShrink={0} w="max-content">
								<button onclick={props.onDelete} type="button">
									<FaRegularTrashCan />
								</button>
							</Column>
						</Show>
					</Row>
				)}
			</Show>
		</Column>
	);
};
export default Record;
